import { Grid, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import BadgeDetached from "@entrilia/shared/components/BadgeDetached";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import { ReportTypesEnum } from "../types/reportTypes";
import type { ReportListItem } from "./reportListFilter";
import { filterReportList } from "./reportListFilter";

interface Props<T extends ReportListItem> {
  items: T[];
  getItemId: (item: T) => string;
  children: (filteredItems: T[]) => ReactNode;
}

const FilteredReportList = <T extends ReportListItem>({ items, getItemId, children }: Props<T>) => {
  const [selectedType, setSelectedType] = useState<ReportTypesEnum>(ReportTypesEnum.All);
  const [searchText, setSearchText] = useState("");

  const itemsByType = useMemo(() => filterReportList(items, searchText, getItemId), [items, searchText, getItemId]);

  return (
    <Stack
      spacing={2}
      useFlexGap
      sx={{
        flex: 1,
        px: 3,
        pb: 1,
        backgroundColor: "background.paper",
        width: "100%",
      }}
    >
      <Grid container sx={{ justifyContent: "space-between", width: "100%" }}>
        <ToggleButtonGroup
          value={selectedType}
          exclusive
          onChange={(_, value: ReportTypesEnum) => setSelectedType((prev) => value || prev)}
        >
          <ToggleButton value={ReportTypesEnum.All} sx={{ gap: 0.5 }}>
            All <BadgeDetached showZero badgeContent={itemsByType[ReportTypesEnum.All].length} color="secondary" />
          </ToggleButton>
          <ToggleButton value={ReportTypesEnum.MyReports} sx={{ gap: 0.5 }}>
            Created by Me
            <BadgeDetached showZero badgeContent={itemsByType[ReportTypesEnum.MyReports].length} color="secondary" />
          </ToggleButton>
          <ToggleButton value={ReportTypesEnum.SharedWithMe} sx={{ gap: 0.5 }}>
            Accessible to Me
            <BadgeDetached showZero badgeContent={itemsByType[ReportTypesEnum.SharedWithMe].length} color="secondary" />
          </ToggleButton>
        </ToggleButtonGroup>
        <SearchField onSearch={setSearchText} debounceTimeMs={300} />
      </Grid>
      <Grid container sx={{ flex: 1, position: "relative", width: "100%" }}>
        {children(itemsByType[selectedType])}
      </Grid>
    </Stack>
  );
};

export default FilteredReportList;
